import { motion } from "framer-motion";
import { Calendar, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

const TIMMA_URL = "https://bestill.timma.no/beautybykuleva";

export default function BookingPage() {
  const { t } = useLanguage();
  
  return (
    <div className="pt-20 min-h-screen bg-[#F9F5F6]">
      {/* Hero Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-[#F9F5F6] flex items-center justify-center">
                <Calendar size={24} className="text-[#D4A5A5]" />
              </div>
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4A4A4A] mb-4">
              {t("booking.title")} <span className="italic text-[#D4A5A5]">{t("booking.titleHighlight")}</span>
            </h1>
            <p className="text-[#8C8C8C] text-base sm:text-lg mb-6">
              {t("booking.subtitle")}
            </p>
            <a
              href={TIMMA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#D4A5A5] hover:underline text-sm"
              data-testid="open-timma-external"
            >
              <ExternalLink size={14} />
              {t("booking.openExternal")}
            </a>
          </motion.div>
        </div>
      </section>

      {/* Timma Iframe Section */}
      <section className="py-8 sm:py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-soft overflow-hidden"
          >
            {/* Iframe Container */}
            <div className="relative w-full" style={{ minHeight: "800px" }}>
              <iframe
                src={TIMMA_URL}
                title="Book Appointment with Beauty by Kuleva"
                className="absolute inset-0 w-full h-full border-0"
                style={{ minHeight: "800px" }}
                allow="payment"
                loading="lazy"
                data-testid="timma-booking-iframe"
              />
            </div>
          </motion.div>

          {/* Alternative booking option */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center mt-8"
          >
            <p className="text-[#8C8C8C] text-sm mb-4">
              {t("booking.trouble")}
            </p>
            <a
              href={TIMMA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="timma-fallback-btn"
            >
              <Button
                variant="outline"
                className="border-[#D4A5A5] text-[#D4A5A5] hover:bg-[#D4A5A5] hover:text-white rounded-full px-8 py-6"
              >
                <ExternalLink size={16} className="mr-2" />
                {t("booking.bookOnTimma")}
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#4A4A4A] mb-4">
            {t("booking.needHelp")}
          </h2>
          <p className="text-[#8C8C8C] mb-6">
            {t("booking.needHelpDescription")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            <a
              href="tel:+4793046396"
              className="text-[#D4A5A5] font-medium hover:underline"
            >
              +47 930 46 396
            </a>
            <span className="hidden sm:inline text-[#E8D0D0]">|</span>
            <a
              href="mailto:post@beautybykuleva.no"
              className="text-[#D4A5A5] font-medium hover:underline"
            >
              post@beautybykuleva.no
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
